import { jsx, jsxs } from "react/jsx-runtime";
import styled, { css } from "styled-components";
import { typographyCss, cbSemanticCssVars, colors } from "@creditornot/cb-ingredients";
import { Tooltip } from "@creditornot/cb-popover";
import { forwardRef } from "react";
import { createThemedCssVarDefinitions } from "@creditornot/cb-utils";
const InfoCircleFilledIcon = forwardRef(
  (props, ref) => /* @__PURE__ */ jsx("svg", { viewBox: "0 0 24 24", ...props, ref, children: /* @__PURE__ */ jsx("path", { d: "M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12C23.993 5.376 18.624.007 12 0zm.25 5a1.5 1.5 0 110 3 1.5 1.5 0 010-3zm2.25 13.5h-4a1 1 0 010-2h.75a.25.25 0 00.25-.25v-4.5a.25.25 0 00-.25-.25h-.75a1 1 0 010-2h1a2 2 0 012 2v4.75c0 .138.112.25.25.25h.75a1 1 0 010 2z" }) })
);
const responsiveContainerCss = css`
  padding-inline-start: 24px;
  padding-inline-end: 24px;

  @media (max-width: 550px) {
    padding-inline-start: 12px;
    padding-inline-end: 12px;
  }
`;
const Root$3 = styled.div`
  box-sizing: border-box;
  padding-top: 24px;
  padding-bottom: 24px;
  display: flex;
  align-items: stretch;
  ${typographyCss.Body3()}
  ${({ $horizontalPadding }) => $horizontalPadding && responsiveContainerCss}

  & + & {
    border-top: 1px solid ${cbSemanticCssVars.borderLight};
  }

  @media (max-width: 550px) {
    flex-direction: column;
  }
`;
const LeftCol$2 = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: ${({ $labelAlign }) => $labelAlign.startsWith("top") ? "flex-start" : "center"};
  flex-shrink: 0;
  box-sizing: border-box;
  width: 200px;
  padding-top: ${({ $labelAlign }) => $labelAlign === "top-pad" ? "8px" : "0"};
  padding-inline-end: 24px;

  @media (max-width: 550px) {
    padding-top: 0;
  }
`;
const RightCol$2 = styled.div`
  flex: 1;
  min-width: 0;

  @media (max-width: 550px) {
    margin-top: 8px;
  }
`;
const LabelContainer = styled.div`
  display: flex;
  align-items: ${({ $labelContentAlign }) => $labelContentAlign === "top" ? "flex-start" : "center"};
`;
const Label$1 = styled.label`
  color: ${({ $withLeftColContent }) => $withLeftColContent ? cbSemanticCssVars.textPrimary : cbSemanticCssVars.textSecondary};
`;
const Asterisk$1 = styled.span`
  color: ${colors.strawberry100};
`;
const InfoContainer = styled.div`
  margin-inline-start: 12px;
  display: flex;
  align-items: center;
`;
const InfoIcon = styled(InfoCircleFilledIcon)`
  width: 12px;
  fill: ${cbSemanticCssVars.textSecondary};
`;
const LeftColContent = styled.div`
  margin-block-start: 8px;

  @media (max-width: 550px) {
    margin-block-end: 8px;
  }
`;
const FormRow = ({
  children,
  className,
  horizontalPadding = false,
  info,
  label,
  labelAlign = "top",
  labelContentAlign = "center",
  labelHtmlFor,
  leftColContent,
  leftColStyle,
  required,
  rightColStyle,
  style
}) => /* @__PURE__ */ jsxs(Root$3, { $horizontalPadding: horizontalPadding, className, style, children: [
  /* @__PURE__ */ jsxs(LeftCol$2, { $labelAlign: labelAlign, style: leftColStyle, children: [
    /* @__PURE__ */ jsxs(LabelContainer, { $labelContentAlign: labelContentAlign, children: [
      /* @__PURE__ */ jsxs(Label$1, { $withLeftColContent: !!leftColContent, htmlFor: labelHtmlFor, children: [
        label,
        required && /* @__PURE__ */ jsx(Asterisk$1, { children: " *" })
      ] }),
      !!info && /* @__PURE__ */ jsx(Tooltip, { content: info, children: /* @__PURE__ */ jsx(InfoContainer, { children: /* @__PURE__ */ jsx(InfoIcon, {}) }) })
    ] }),
    leftColContent !== void 0 && leftColContent !== null && /* @__PURE__ */ jsx(LeftColContent, { children: leftColContent })
  ] }),
  /* @__PURE__ */ jsx(RightCol$2, { style: rightColStyle, children })
] });
const Root$2 = styled.div`
  box-sizing: border-box;
  padding-top: 16px;
  padding-bottom: 16px;
  min-height: 68px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  ${({ $horizontalPadding }) => $horizontalPadding && responsiveContainerCss}
  border-bottom: 1px solid ${cbSemanticCssVars.borderLight};
`;
const LeftCol$1 = styled.div`
  display: flex;
  flex: 1;
  padding-inline-end: 24px;
  overflow: hidden;
`;
const RightCol$1 = styled.div`
  display: flex;
  justify-content: flex-end;
  text-align: end;
  ${typographyCss.Body3()}
`;
const RightContentContainer = styled.div``;
const Heading = styled.h6`
  ${typographyCss.Title1()}
  margin-top: 0;
  margin-bottom: 0;
  display: block;
  box-sizing: border-box;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const FormHeader = ({
  children,
  className,
  headingElement = "h6",
  horizontalPadding = false,
  rightContent,
  style
}) => /* @__PURE__ */ jsxs(Root$2, { $horizontalPadding: horizontalPadding, className, style, children: [
  /* @__PURE__ */ jsx(LeftCol$1, { children: /* @__PURE__ */ jsx(Heading, { as: headingElement, children }) }),
  /* @__PURE__ */ jsx(RightCol$1, { children: /* @__PURE__ */ jsx(RightContentContainer, { children: rightContent }) })
] });
const HelpCircleFilledIcon = forwardRef(
  (props, ref) => /* @__PURE__ */ jsx("svg", { viewBox: "0 0 24 24", ...props, ref, children: /* @__PURE__ */ jsx("path", { d: "M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12C23.993 5.376 18.624.007 12 0zm0 19a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm1.6-6.08a1 1 0 00-.6.917 1 1 0 01-2 0 3 3 0 011.8-2.75A2 2 0 1010 9.255a1 1 0 11-2 0 4 4 0 115.6 3.666v-.001z" }) })
);
const Root$1 = styled.div`
  display: flex;
`;
const Col = styled.div`
  display: flex;
  flex-direction: column;
`;
const LeftCol = styled(Col)`
  flex-shrink: 0;
  padding-inline-end: 8px;
`;
const RightCol = styled(Col)`
  flex: 1;
  overflow: hidden;
`;
const IconContainer = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  height: 16px;
`;
const Message = styled.div`
  max-width: 100%;
  text-overflow: ellipsis;
  ${typographyCss.Small()}
`;
const StyledInfoCircleFilledIcon = styled(InfoCircleFilledIcon)`
  ${createThemedCssVarDefinitions({
  iconColor: { light: colors.space100, dark: colors.salt100 }
})}

  display: block;
  width: 12px;
  fill: var(--iconColor);
`;
const InputContainerInfoAlert = ({ children, className, style }) => /* @__PURE__ */ jsxs(Root$1, { className, style, children: [
  /* @__PURE__ */ jsx(LeftCol, { children: /* @__PURE__ */ jsx(IconContainer, { children: /* @__PURE__ */ jsx(StyledInfoCircleFilledIcon, {}) }) }),
  /* @__PURE__ */ jsx(RightCol, { children: /* @__PURE__ */ jsx(Message, { children }) })
] });
const Root = styled.div``;
const LabelRow = styled.div`
  margin-bottom: 8px;
  display: flex;
  align-items: center;
`;
const Label = styled.label`
  color: ${cbSemanticCssVars.textPrimary};
  ${typographyCss.Body3()}
`;
const DescriptionPopoverContainer = styled.div`
  flex-shrink: 0;
  margin-inline-start: 8px;
  display: flex;
  align-items: center;
`;
const DescriptionTooltipIcon = styled(HelpCircleFilledIcon)`
  width: 12px;
  fill: ${cbSemanticCssVars.textPrimary};
`;
const ContentRow = styled.div``;
const HintRow = styled.div`
  display: flex;
  margin-top: 8px;
`;
const HintRowLeftCol = styled.div`
  flex: 1;
`;
const HintRowRightCol = styled.div`
  flex-shrink: 0;
  padding-inline-start: 12px;
  text-align: end;
`;
const CharacterCount = styled.div`
  color: ${cbSemanticCssVars.textSecondary};
  ${typographyCss.Small()}
`;
const Asterisk = styled.span`
  color: ${colors.strawberry100};
`;
const InputContainer = ({
  characterCount,
  children,
  className,
  description,
  hint,
  label,
  labelHtmlFor,
  required,
  style
}) => /* @__PURE__ */ jsxs(Root, { className, style, children: [
  (!!label || !!description) && /* @__PURE__ */ jsxs(LabelRow, { children: [
    /* @__PURE__ */ jsxs(Label, { htmlFor: labelHtmlFor, children: [
      label,
      required && /* @__PURE__ */ jsx(Asterisk, { children: " *" })
    ] }),
    !!description && /* @__PURE__ */ jsx(DescriptionPopoverContainer, { children: /* @__PURE__ */ jsx(Tooltip, { content: description, children: /* @__PURE__ */ jsx(DescriptionTooltipIcon, {}) }) })
  ] }),
  /* @__PURE__ */ jsx(ContentRow, { children }),
  (!!hint || !!characterCount) && /* @__PURE__ */ jsxs(HintRow, { children: [
    /* @__PURE__ */ jsx(HintRowLeftCol, { children: !!hint && /* @__PURE__ */ jsx(InputContainerInfoAlert, { children: hint }) }),
    /* @__PURE__ */ jsx(HintRowRightCol, { children: !!characterCount && /* @__PURE__ */ jsx(CharacterCount, { children: `${characterCount.value} / ${characterCount.max}` }) })
  ] })
] });
export {
  FormHeader,
  FormRow,
  InputContainer
};
//# sourceMappingURL=index.js.map
